import React from 'react';
import { Warning } from '@phosphor-icons/react';

/**
 * Confirmation modal shown after a dry-run compliance sweep returns its preview.
 * Receives its state from the parent AdminPage. Kept as a standalone component
 * so it can be mounted at the root of AdminPage's JSX (not nested inside a
 * conditionally-rendered tab) — otherwise the modal cannot render when the
 * admin is on a different tab.
 *
 * Props:
 *  - dryRun: object with { total_scanned, would_delete, by_platform, by_reason }
 *  - running: bool — true while delete is firing
 *  - onCancel: () => void
 *  - onFire: () => void — calls runComplianceSweep(false)
 */
const SweepConfirmModal = ({ dryRun, running, onCancel, onFire }) => {
  if (!dryRun) return null;

  return (
    <>
      <style>{`
        @keyframes sweepPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(239,68,68,0.6), inset 0 0 20px rgba(0,0,0,0.3); transform: scale(1); }
          50%      { box-shadow: 0 0 55px rgba(239,68,68,0.95), inset 0 0 20px rgba(0,0,0,0.3); transform: scale(1.03); }
        }
        [data-testid="compliance-sweep-btn"]:not(:disabled) { animation: sweepPulse 2s ease-in-out infinite; }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4" data-testid="sweep-confirm-modal">
        <div className="bg-slate-900 border-2 border-red-500/50 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto overscroll-contain">
          <div className="flex items-center gap-3 mb-4">
            <Warning weight="fill" className="w-7 h-7 text-red-500" />
            <h3 className="font-heading text-xl font-bold text-white">Sweep — Dry-Run Result</h3>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-slate-800/60 rounded-lg p-3 text-center">
              <div className="text-2xl font-black text-white">{dryRun.total_scanned}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Scanned</div>
            </div>
            <div className="bg-red-500/10 rounded-lg p-3 text-center ring-1 ring-red-500/30">
              <div className="text-2xl font-black text-red-400">{dryRun.would_delete}</div>
              <div className="text-[10px] uppercase tracking-wider text-red-300">Would Delete</div>
            </div>
            <div className="bg-emerald-500/10 rounded-lg p-3 text-center ring-1 ring-emerald-500/30">
              <div className="text-2xl font-black text-emerald-400">{dryRun.total_scanned - dryRun.would_delete}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-300">Survive</div>
            </div>
          </div>

          {dryRun.by_platform && Object.keys(dryRun.by_platform).length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">By platform</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(dryRun.by_platform).map(([k, v]) => (
                  <span key={k} className="px-2 py-1 bg-slate-800 rounded-md text-xs text-slate-300">
                    <span className="text-slate-500">{k}:</span> <span className="font-bold text-white">{v}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {dryRun.by_reason && Object.keys(dryRun.by_reason).length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Reasons</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {Object.entries(dryRun.by_reason).map(([reason, n]) => (
                  <div key={reason} className="flex justify-between text-xs">
                    <span className="text-slate-400 truncate pr-2">{reason}</span>
                    <span className="text-red-400 font-bold flex-shrink-0">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mb-4">
            <strong>This is a dry-run.</strong> Nothing has been deleted. Click &quot;Fire For Real&quot; below to permanently remove the {dryRun.would_delete} flagged items.
          </p>

          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold rounded-lg"
              data-testid="sweep-cancel-btn">
              Cancel — Don&apos;t Delete
            </button>
            <button
              onClick={onFire}
              disabled={running || dryRun.would_delete === 0}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              data-testid="sweep-fire-btn">
              <Warning weight="fill" className="w-4 h-4" />
              {running ? 'Deleting…' : `Fire For Real (${dryRun.would_delete})`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SweepConfirmModal;
